import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { DashboardContent } from '@/layouts/dashboard';
import { Loader } from '@/components/custom/loader';
import { Badge } from '@/components/ui/badge';
import { liveChallengeActivationsApi, liveChallengesApi } from '@/services/liveChallenges';
import { useLiveChallenge } from '@/hooks/useLiveChallenge';
import LobbyView from './LobbyView';
import QuestionView from './QuestionView';
import QuestionResultView from './QuestionResultView';
import FinalView from './FinalView';
import { Button } from '@/components/ui/button';
import { FiCopy } from 'react-icons/fi';

const PHASE_BADGE = {
  lobby: { label: 'Lobby', variant: 'warning' },
  questionActive: { label: 'Em Andamento', variant: 'primary-light' },
  questionResult: { label: 'Em Andamento', variant: 'primary-light' },
  final: { label: 'Finalizado', variant: 'success-light' }
};

/**
 * State machine for the control panel:
 *   lobby -> questionActive -> questionResult -> (loop back to questionActive OR final)
 */
export default function ControlPanel() {
  const { id } = useParams();
  const [challenge, setChallenge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState('lobby');

  const { subscribe, unsubscribe, sessionStatus, currentQuestion, questionResult, questionIndex, totalQuestions, setSessionStatus } = useLiveChallenge();

  const breadcrumbs = {
    title: 'Aprendizado Dinâmico',
    nav: [
      { route: '/admin/desafio-ao-vivo', name: 'Desafio ao Vivo' },
      { route: '#', name: 'Painel de Controle', isActive: true }
    ]
  };

  const normalizeJsonApi = item => {
    if (!item?.attributes) return item;
    return {
      id: item.id,
      ...Object.fromEntries(Object.entries(item.attributes).map(([k, v]) => [k.replace(/-/g, '_'), v]))
    };
  };

  useEffect(() => {
    const fetchChallenge = async () => {
      setLoading(true);
      try {
        const response = await liveChallengeActivationsApi.show(id);
        const raw = response?.data ?? response;
        const resolved = normalizeJsonApi(raw);
        setChallenge(resolved);

        // Preview pré-reveal (próxima questão + destaque da correta + lista de próximas):
        // só o template (LiveChallengeSerializer) expõe live_challenge_questions, e só pro
        // dono do template (gate por current_user_id no backend). Encadeado após o show da
        // ativação porque depende do live_challenge_id dela. Falha (host não-dono, 404, etc.)
        // degrada em silêncio — QuestionView cai no current_question da própria ativação.
        if (resolved.live_challenge_id) {
          liveChallengesApi
            .show(resolved.live_challenge_id)
            .then(templateResponse => {
              const templateRaw = templateResponse?.data ?? templateResponse;
              const template = normalizeJsonApi(templateRaw);
              if (template.live_challenge_questions) {
                setChallenge(prev => (prev ? { ...prev, live_challenge_questions: template.live_challenge_questions } : prev));
              }
            })
            .catch(() => {});
        }

        if (resolved.status === 'finished') {
          setPhase('final');
          setSessionStatus('finished');
        } else if (resolved.status === 'in_progress') {
          // in_progress cobre dois sub-estados que mapeiam para telas diferentes:
          //  - questionActive: há uma questão revelada/ativa em andamento;
          //  - questionResult: a questão atual já foi encerrada e o admin está na
          //    tela de resultado (que tem "Próxima Questão"/"Finalizar Desafio").
          // O show já devolve a evidência: last_question_result presente (questão
          // encerrada aguardando próximo reveal) ou current_question ausente (todas
          // encerradas, aguardando Finalizar). Derivamos a fase daqui para entrar
          // direto na tela certa, sem flash de QuestionView até o resync assíncrono
          // do provider corrigir. (AVA-500)
          const awaitingResult = !!resolved.last_question_result || !resolved.current_question;
          setPhase(awaitingResult ? 'questionResult' : 'questionActive');
          setSessionStatus('in_progress');
        } else {
          setPhase('lobby');
        }
      } catch {
        toast.error('Erro ao carregar desafio.');
      } finally {
        setLoading(false);
      }
    };

    fetchChallenge();
  }, [id]);

  useEffect(() => {
    if (!id) return;
    subscribe(id, 'teacher');
    return () => {
      unsubscribe();
    };
  }, [id, subscribe, unsubscribe]);

  useEffect(() => {
    if (sessionStatus === 'in_progress' && phase === 'lobby') {
      setPhase('questionActive');
    }
    if (sessionStatus === 'finished') {
      setPhase('final');
    }
  }, [sessionStatus, phase]);

  useEffect(() => {
    if (currentQuestion && phase !== 'final') {
      setPhase('questionActive');
    }
  }, [currentQuestion]);

  useEffect(() => {
    if (questionResult && phase === 'questionActive') {
      setPhase('questionResult');
    }
  }, [questionResult, phase]);

  const handleNextQuestion = () => {
    setPhase('questionActive');
  };

  if (loading) return <Loader />;

  const phaseBadge = PHASE_BADGE[phase] || PHASE_BADGE.lobby;

  const accessCode = challenge?.access_code || '';
  const qrUrl = `${window.location.origin}/desafio-ao-vivo/entrar?code=${accessCode}`;

  // Provider hidrata index/total via resync (assíncrono). Ao entrar direto em
  // questionResult pós-remount (AVA-500), esses valores ainda são 0 — usar só eles
  // marcaria toda questão como "última" e mostraria "Finalizar Desafio" indevido
  // numa questão intermediária. Cai para o payload REST até o provider hidratar.
  const effectiveIndex = totalQuestions > 0 ? questionIndex : (challenge?.current_question_index ?? questionIndex);
  const effectiveTotal = totalQuestions > 0 ? totalQuestions : (challenge?.total_questions ?? totalQuestions);
  const isLastQuestion = effectiveTotal > 0 && effectiveIndex + 1 >= effectiveTotal;

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-bold text-foreground">{challenge?.title || 'Painel de Controle'}</h2>

        <div className="flex items-center gap-2">
          {phase !== 'lobby' && (
            <Button
              type="button"
              variant="outline"
              size="xs"
              onClick={() => {
                navigator.clipboard.writeText(qrUrl);
                toast.success('Link copiado!');
              }}
            >
              <FiCopy /> Copiar link do desafio
            </Button>
          )}

          <Badge
            variant={phaseBadge.variant}
            size="default"
          >
            {phaseBadge.label}
          </Badge>
        </div>
      </div>

      {phase === 'lobby' && (
        <LobbyView
          challenge={challenge}
          activationId={id}
          onPhaseChange={setPhase}
        />
      )}

      {phase === 'questionActive' && (
        <QuestionView
          challenge={challenge}
          activationId={id}
          onPhaseChange={setPhase}
        />
      )}

      {phase === 'questionResult' && (
        <QuestionResultView
          activationId={id}
          isLastQuestion={isLastQuestion}
          onNextQuestion={handleNextQuestion}
        />
      )}

      {phase === 'final' && <FinalView activationId={id} />}
    </DashboardContent>
  );
}
